import React from 'react';
import {
  Avatar,
  AvatarGroup,
  Clickable,
  Drawer,
  Icon,
  IconButton,
  P,
  Person,
  Popover,
  Tabs,
} from '@fashionnovateam/nova-ui-toolkit';
import { useRequestedItemsContext } from '../hooks/useRequestedItems';
import { useCurrentUser } from '../../../Lib/Contexts/CurrentUserContext';
import { useReorderChatParticipants } from '../../../Lib/Hooks/reorder-chat.hooks';
import { QuoteTab } from './QuoteTab';
import { ChatTab } from './ChatTab';

const DRAWER_WIDTH = '35%';

function DrawerHeader({ onClose }: { onClose: () => void }) {
  const { selectedItem, setSelectedItem, getNextItem, getPrevItem } =
    useRequestedItemsContext();

  const handleNext = () => {
    const nextItem = getNextItem();
    if (nextItem) {
      setSelectedItem(nextItem);
    }
  };

  const handlePrev = () => {
    const prevItem = getPrevItem();
    if (prevItem) {
      setSelectedItem(prevItem);
    }
  };

  if (!selectedItem) {
    return null;
  }

  const vendorName = selectedItem.vendor_name || '-';
  const productName = selectedItem.style_name || '';
  const productColor = selectedItem.style_color || '';
  const productDisplay = [productName, productColor]
    .filter(Boolean)
    .join(' - ');
  const photoUrl = selectedItem.photos?.[0];

  return (
    <div className="flex-shrink-0 flex items-center border-b border-neutral-200 py-3 px-5 gap-2 w-full bg-white">
      <IconButton
        label="Close"
        size="md"
        icon={<Icon name="sidebar/sidebar-right-close" />}
        onClick={onClose}
        color="secondary"
      />

      <div className="grow min-w-0 gap-3 flex flex-row items-center">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={productName}
            className="w-8 h-8 rounded-md object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-8 h-8 bg-neutral-200 rounded-md flex items-center justify-center flex-shrink-0">
            <Icon.FileSearch className="text-neutral-400" />
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <P size="sm" weight="medium" className="truncate">
            {vendorName}
          </P>
          <P size="2xs" weight="normal" color="disabled" className="truncate">
            {productDisplay || '-'}
          </P>
        </div>
      </div>

      <div className="flex items-end gap-1">
        <IconButton
          label="Previous item"
          color="secondary"
          icon={<Icon.ChevronUp />}
          type="button"
          onClick={handlePrev}
          disabled={!getPrevItem()}
        />
        <IconButton
          label="Next item"
          color="secondary"
          icon={<Icon.ChevronDown />}
          type="button"
          onClick={handleNext}
          disabled={!getNextItem()}
        />
      </div>
    </div>
  );
}

function DrawerTabs() {
  const { drawerTab, setDrawerTab, selectedItem, userId } =
    useRequestedItemsContext();
  const { participants, isLoading } = useReorderChatParticipants(
    userId,
    selectedItem?.style_color,
  );

  return (
    <div className="flex-shrink-0 border-b border-neutral-200 px-5 flex justify-between items-center bg-white">
      <Tabs.Group className="border-b-0">
        <Clickable onClick={() => setDrawerTab('chat')}>
          <Tabs.Item isActive={drawerTab === 'chat'}>Chat</Tabs.Item>
        </Clickable>
        <Clickable onClick={() => setDrawerTab('quote')}>
          <Tabs.Item isActive={drawerTab === 'quote'}>Quote</Tabs.Item>
        </Clickable>
      </Tabs.Group>

      <div className="min-w-[100px] flex justify-end">
        {!isLoading && participants.length > 0 && (
          <Popover position="bottom-right" offset={8}>
            {({ open: openPopover, floating }) => (
              <>
                <div ref={floating.refs.setReference}>
                  <AvatarGroup max={5} onRemainingClick={openPopover}>
                    {participants.map((p) => (
                      <Avatar
                        key={p.id}
                        name={p.name}
                        badgeType={p.badgeType}
                      />
                    ))}
                  </AvatarGroup>
                </div>
                <Popover.Content className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                  <div className="flex flex-col gap-2">
                    <P size="sm" weight="semibold">
                      Participants
                    </P>
                    {participants.map((p) => (
                      <div
                        key={p.id}
                        className="py-2 border-b border-neutral-200 last:border-b-0"
                      >
                        <Person name={p.name} badgeType={p.badgeType} />
                      </div>
                    ))}
                  </div>
                </Popover.Content>
              </>
            )}
          </Popover>
        )}
        {!isLoading && participants.length === 0 && (
          <P size="sm" color="disabled">
            -
          </P>
        )}
      </div>
    </div>
  );
}

function ChatTabContent({ vendorId }: { vendorId: string }) {
  const { selectedItem, userId } = useRequestedItemsContext();

  if (!selectedItem?.style_color) {
    return (
      <div className="flex-1 flex items-center justify-center p-5">
        <P size="sm" color="disabled">
          No item selected
        </P>
      </div>
    );
  }

  return (
    <ChatTab
      key={selectedItem.style_color}
      vendorId={vendorId}
      styleColor={selectedItem.style_color}
      userId={userId}
    />
  );
}

type RequestedItemsDrawerProps = {
  vendorId?: string;
};

export function RequestedItemsDrawer({
  vendorId: vendorIdProp,
}: RequestedItemsDrawerProps = {}) {
  const { isDrawerOpen, closeDrawer, selectedItem, drawerTab } =
    useRequestedItemsContext();
  const vendorIdFromContext =
    useCurrentUser().currentUser?.vendor?.internal_id ?? '';
  const vendorId = vendorIdProp ?? vendorIdFromContext;

  const renderTabContent = () => {
    if (drawerTab === 'chat') {
      return <ChatTabContent vendorId={vendorId} />;
    }

    return <QuoteTab />;
  };

  return (
    <Drawer
      noMargin
      width={DRAWER_WIDTH}
      isOpen={isDrawerOpen && !!selectedItem}
      noBackdrop
    >
      <Drawer.Content className="p-0 h-full" data-testid="drawer">
        {selectedItem && (
          <div className="h-full flex flex-col overflow-hidden">
            <DrawerHeader onClose={closeDrawer} />
            <DrawerTabs />
            {renderTabContent()}
          </div>
        )}
      </Drawer.Content>
    </Drawer>
  );
}
